/**
 * `resource-cache-invalidation.ts` — phase 67. Shared helpers the SPA's
 * mutation sites call after a successful write so the cache never serves
 * data a write just changed (sub-decision D).
 *
 * The cache keys are namespaced strings:
 *
 *   - `data:<entity>:<cursor>:<filters>` — Data page entity rows
 *   - `data-counts`                      — Data sidebar row counts
 *   - `catalog` / `catalog:browsable-entities` — Catalog / Mcp / Data picker
 *   - `sagas`                            — Flows page "Last run" column
 *   - `flows-stats`                      — Flows page stats
 *   - `lookup:<entity>`                  — SchemaForm lookup dropdowns
 *   - `studio-config:<slug>` / `studio-secrets:<slug>` — config editor
 *
 * `invalidateAfterMutation()` is the broad default: a generic
 * `dispatch`/`useDispatch` run can touch nearly any entity, so we
 * over-invalidate every read view (the next read of each refetches). The
 * narrower exports exist for the few sites that know exactly which
 * resources they changed.
 */

import { invalidate } from "./resource-cache";

/**
 * Broad invalidation after a generic canonical-action dispatch. Clears
 * every cached read resource so no page renders pre-write data. Cheap
 * (marks entries stale; the refetch only happens when a page next reads
 * the key).
 */
export function invalidateAfterMutation(): void {
  invalidate("data:");
  invalidate("data-counts");
  invalidate("lookup:");
  invalidate("sagas");
  invalidate("flows-stats");
  invalidate("catalog");
}

/** Data-page row edit / dev-reset: clears entity rows + counts. */
export function invalidateDataViews(): void {
  invalidate("data:");
  invalidate("data-counts");
  invalidate("lookup:");
}

/** Flows toggle: clears the flow stats + the catalog (carries `enabled`). */
export function invalidateFlows(): void {
  invalidate("flows-stats");
  invalidate("catalog");
}

/**
 * `/agents` write: a pause/resume, a preload override, or a connector flip.
 *
 * All three are invalidated together on purpose. The board's per-lane status is
 * a JOIN over the control flag, the heartbeat AND the live `studio_config`
 * connector block, so a connector flip that cleared only `studio-config:<slug>`
 * would leave the card claiming the old state until the 30s window lapsed —
 * the operator's own click reading as if it had not landed.
 */
export function invalidateAgents(): void {
  invalidate("agents");
  invalidate("preload");
  invalidate("studio-config:");
}

/** Studio-config save / reload: clears that studio's config + secrets. */
export function invalidateStudioConfig(slug: string): void {
  invalidate(`studio-config:${slug}`);
  invalidate(`studio-secrets:${slug}`);
  // The catalog surfaces chat-translator config derived from the YAML.
  invalidate("catalog");
}
